import { queryOptions, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { VendorRfqCheckResult } from "../Purchaser/RFQ";
import type { JSONContent } from "@tiptap/react";
import PurchaserClient from "../PurchaserClient";
import { useRFQListFilters, type RFQFilterParams } from "../../components/RFQ/List/useRFQListFilters";
import type { RFQListResponse, RFQPatch } from "../Purchaser/RFQ";
import { queryClient, type QueryInvalidateOption } from "./client";

const FIVE_SECONDS = 5 * 1000;

/**
 * RFQ Query Options
 */
export const RFQQueryOptions = {
    RFQ(uniquePublicId?: string | null) {
        return queryOptions({
            queryKey: ["rfq", uniquePublicId],
            queryFn: async () => {
                if (!uniquePublicId) throw new Error("Invalid RFQ ID");
                return await PurchaserClient.getRFQv2(uniquePublicId);
            },
            // refetch every 5 seconds
            refetchInterval: FIVE_SECONDS,
            // do not fetch if the data is not 5 seconds old
            // ^ this only applies for the refetch interval, manually refetching will always refetch
            // this guarantees we're not overlapping interval refetches with manual ones
            staleTime: FIVE_SECONDS,
            enabled: !!uniquePublicId,
        });
    },
    RFQMailboxes(uniquePublicId?: string | null) {
        return queryOptions({
            queryKey: ["rfqMailboxes", uniquePublicId],
            queryFn: async () => {
                if (!uniquePublicId) throw new Error("Invalid RFQ ID");
                return await PurchaserClient.getMailboxes(uniquePublicId);
            },
            refetchInterval: FIVE_SECONDS,
            staleTime: FIVE_SECONDS,
            enabled: !!uniquePublicId,
        });
    },
};

/**
 * RFQ Hooks
 */
export const useRFQ = (uniquePublicId?: string | null) => {
    const query = useQuery(RFQQueryOptions.RFQ(uniquePublicId));
    const { data, refetch, ...rest } = query;

    const rfq = useMemo(() => data, [data]);
    const refetchRFQ = useCallback(() => refetch(), [refetch]);

    return { rfq, refetchRFQ, ...rest };
};

export const useRFQList = (paramFilters?: RFQFilterParams) => {
    const { filters: searchParamFilters } = useRFQListFilters();
    const filters = paramFilters ? paramFilters : searchParamFilters;

    const query = useInfiniteQuery({
        queryKey: ["rfqList", "infinite", filters],
        queryFn: async ({ pageParam }): Promise<RFQListResponse> => {
            return await PurchaserClient.getRFQList({ ...filters, page: pageParam, limit: 50 });
        },
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            if (!lastPage?.pagination) return undefined;
            const { totalPages } = lastPage.pagination;
            return lastPageParam < totalPages ? lastPageParam + 1 : undefined;
        },
        initialPageParam: 1 as number,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        // Keep showing previous results while new filtered data loads,
        // preventing the full-page spinner flash on every filter change.
        placeholderData: prev => prev,
    });

    const rfqs = useMemo(() => query.data?.pages.flatMap(p => p.results) ?? [], [query.data]);
    const stats = query.data?.pages[0]?.stats;

    return {
        ...query,
        rfqs,
        stats,
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
    };
};

export const useRFQMailboxes = (uniquePublicId?: string | null) => {
    const options = RFQQueryOptions.RFQMailboxes(uniquePublicId);
    const queryKey = options.queryKey;
    const query = useQuery(options);

    const { data, refetch, ...rest } = query;

    const mailboxes = useMemo(() => data, [data]);
    const refetchAllMailboxes = useCallback(() => refetch(), [refetch]);

    // Refetch a single mailbox and merge it into the cached array
    const refetchMailbox = useCallback(
        async (mailboxId: number) => {
            if (!uniquePublicId) return;

            const updatedMailbox = await PurchaserClient.getMailbox(mailboxId);

            // Update the cache by replacing just this mailbox in the array
            queryClient.setQueryData(queryKey, oldMailboxes => {
                if (!oldMailboxes || !updatedMailbox) return oldMailboxes;

                return oldMailboxes.map(oldMailbox => {
                    if (updatedMailbox.id === oldMailbox.id) return updatedMailbox;
                    return oldMailbox;
                });
            });
        },
        [uniquePublicId],
    );

    return { mailboxes, refetchAllMailboxes, refetchMailbox, ...rest };
};

/**
 * RFQ Invalidation Functions
 */
export async function invalidateRFQListQueries(option?: QueryInvalidateOption) {
    const queryKey = ["rfqList"];

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

export async function invalidateRFQQueries(params: { uniquePublicId?: string; option?: QueryInvalidateOption }) {
    const { uniquePublicId, option } = params;

    const queryKey = ["rfq"];
    if (uniquePublicId) queryKey.push(uniquePublicId);

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

export async function invalidateRFQMailboxQuery(rfqUniquePublicId: string) {
    const queryKey = ["rfqMailboxes", rfqUniquePublicId];
    await queryClient.invalidateQueries({ queryKey });
}

/**
 * RFQ Mutations
 */
export const useCreateRFQ = () => {
    const mutation = useMutation({
        mutationFn: async (params?: { title?: string; contentJson?: JSONContent }) => {
            const title = params?.title ?? "";
            const contentJson = params?.contentJson ?? {};
            return await PurchaserClient.createRFQv2(title, contentJson);
        },
        async onSuccess() {
            await invalidateRFQListQueries();
        },
        onError(e) {
            console.error("Error creating RFQ", e);
        },
    });

    type CreateRFQMutationOptions = Parameters<typeof mutation.mutate>[1];

    return {
        createRFQ: (options?: CreateRFQMutationOptions) => mutation.mutate(undefined, options),
        createRFQWithParams: mutation.mutate,
        isCreatingRFQ: mutation.isPending,
    };
};

export const useUpdateRFQ = () => {
    const mutation = useMutation({
        mutationFn: async (params: { uniquePublicId: string; payload: RFQPatch }) => {
            await PurchaserClient.updateRFQ(params);
            return params.uniquePublicId;
        },
        async onSuccess(uniquePublicId) {
            await invalidateRFQListQueries();
            await invalidateRFQQueries({ uniquePublicId });
        },
        onError(e, params) {
            console.error(`Error updating RFQ with payload: ${JSON.stringify(params.payload)}`, e);
        },
    });

    return {
        updateRFQ: mutation.mutate,
        isUpdatingRFQ: mutation.isPending,
    };
};

export const useVendorRfqCheck = (emails: string[]) => {
    const sortedEmails = useMemo(() => [...emails].filter(Boolean).sort(), [emails]);
    return useQuery<VendorRfqCheckResult>({
        queryKey: ["vendorRfqCheck", sortedEmails],
        queryFn: () => PurchaserClient.checkVendorOnRfqs(sortedEmails),
        enabled: sortedEmails.length > 0,
        staleTime: 30_000,
    });
};
